// DrawManager 상태 관리 훅
// src/hooks/useDrawManager.ts

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { parseDateOnly } from '@/lib/drawHistory';
import type { DrawRecord, SyncLog, ExcelPreviewRow, DrawMode, DbSummary } from '@/pages/admin/components/draw/types';
import {
  AUTO_SYNC_STORAGE_KEY,
  getLatestExpectedRound,
  parseNumbers,
  parseExcelFile,
} from '@/utils/lottoUtils';
import {
  aggregateSyncChain,
  parseApiResponse,
  type ApiBatchResult,
  type ChainResult,
} from '@/lib/syncResult';
import {
  createExcelImportPlan,
  formatExcelImportSuccessMessage,
  type ExcelDuplicateMode,
  type ExcelImportPlan,
} from '@/lib/excelImportPlan';

const DRAWS_PAGE_SIZE = 20;
const SYNC_LOGS_PAGE_SIZE = 15;
const CHUNK_SIZE = 50; // 서버 1회 최대 회차와 동일
const MAX_ROUND = 2000;
const MAX_EXCEL_FILE_BYTES = 10 * 1024 * 1024;

// ---- callApiBatch (Edge Function 기반, 훅 외부 독립 함수) ----

function generateRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore, fallback below
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type InvokeOutcome = { kind: 'response'; result: ApiBatchResult } | { kind: 'uncertain' };

interface InvokeErrorInfo {
  status?: number;
  errorCode?: string;
}

// supabase functions.invoke 의 non-2xx 오류 객체에서 상태·오류 코드를 안전하게 읽는다.
async function extractInvokeErrorInfo(err: unknown): Promise<InvokeErrorInfo> {
  const info: InvokeErrorInfo = {};
  const ctx = (err as { context?: unknown } | null | undefined)?.context;
  if (ctx && typeof ctx === 'object') {
    const resp = ctx as { status?: unknown; json?: () => Promise<unknown> };
    if (typeof resp.status === 'number') info.status = resp.status;
    if (typeof resp.json === 'function') {
      try {
        const body = await resp.json();
        if (body && typeof body === 'object') {
          const code = (body as Record<string, unknown>).error;
          if (typeof code === 'string') info.errorCode = code;
        }
      } catch {
        // body 파싱 실패는 무시(상태 코드만으로 분류)
      }
    }
  }
  return info;
}

function plainError(msg: string): ApiBatchResult {
  return { status: 'error', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg };
}

function abortedResult(): ApiBatchResult {
  return { status: 'aborted', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg: '중단됨' };
}

// 네트워크 응답 불확실성(서버가 처리했는지 알 수 없는 경우)을 구분해 반환.
// invoke API 가 AbortController signal 을 지원하지 않으므로, 중단은 "현재 요청 완료 후 정지"로 처리한다.
async function invokeSyncOnce(
  body: Record<string, unknown>,
  expectedRounds: number[],
  abortRef: { current: boolean },
): Promise<InvokeOutcome> {
  if (abortRef.current) {
    return { kind: 'uncertain' };
  }
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('fetch-lotto-history', { body });
    if (invokeError) {
      const info = await extractInvokeErrorInfo(invokeError);
      const status = info.status;
      if (status) {
        if (status === 401 || status === 403) {
          return { kind: 'response', result: plainError('인증 또는 권한 오류가 발생했습니다. 관리자 권한을 확인해주세요.') };
        }
        if (status === 409) {
          if (info.errorCode === 'sync_in_progress') {
            return { kind: 'response', result: { status: 'running', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg: '이미 다른 동기화가 진행 중입니다.' } };
          }
          if (info.errorCode === 'idempotency_conflict') {
            return { kind: 'response', result: plainError('요청 정보가 충돌했습니다. 새로 시도해주세요.') };
          }
          return { kind: 'response', result: plainError('요청 충돌이 발생했습니다. 잠시 후 다시 시도해주세요.') };
        }
        if (status === 429) {
          return { kind: 'response', result: plainError('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.') };
        }
        if (status >= 500) {
          return { kind: 'uncertain' };
        }
      }
      if (import.meta.env.DEV) console.error('[useDrawManager] Edge Function invoke error:', invokeError);
      return { kind: 'uncertain' };
    }
    return { kind: 'response', result: parseApiResponse(data, expectedRounds) };
  } catch (e) {
    if (import.meta.env.DEV) console.error('[useDrawManager] invoke 예외:', e);
    return { kind: 'uncertain' };
  }
}

// 네트워크 불확실성 재시도 횟수(총 시도 = 1 + UNCERTAIN_RETRY_MAX). 동일 requestId 를 재사용한다.
const UNCERTAIN_RETRY_MAX = 1;

async function callApiBatch(
  start: number,
  end: number,
  trigger: string,
  requestId: string,
  abortRef: { current: boolean },
): Promise<ApiBatchResult> {
  const body = { startRound: start, endRound: end, triggeredBy: trigger, requestId };
  const expectedRounds: number[] = [];
  for (let r = start; r <= end; r++) expectedRounds.push(r);
  for (let attempt = 0; attempt <= UNCERTAIN_RETRY_MAX; attempt++) {
    if (abortRef.current) {
      return abortedResult();
    }
    const r = await invokeSyncOnce(body, expectedRounds, abortRef);
    if (r.kind === 'response') return r.result;
    if (attempt < UNCERTAIN_RETRY_MAX) {
      await new Promise((r2) => setTimeout(r2, 500));
    }
  }
  return { status: 'error', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg: '서버 응답이 불확실합니다. 다시 시도해주세요.' };
}

async function callApiRounds(
  rounds: number[],
  trigger: string,
  requestId: string,
  abortRef: { current: boolean },
): Promise<ApiBatchResult> {
  const body = { rounds, triggeredBy: trigger, requestId };
  for (let attempt = 0; attempt <= UNCERTAIN_RETRY_MAX; attempt++) {
    if (abortRef.current) {
      return abortedResult();
    }
    const r = await invokeSyncOnce(body, rounds, abortRef);
    if (r.kind === 'response') return r.result;
    if (attempt < UNCERTAIN_RETRY_MAX) {
      await new Promise((r2) => setTimeout(r2, 500));
    }
  }
  return { status: 'error', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg: '서버 응답이 불확실합니다. 다시 시도해주세요.' };
}

// ---- Hook ----

export function useDrawManager() {
  // ── 공통 상태 ──
  const [draws, setDraws] = useState<DrawRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [mode, setMode] = useState<DrawMode>('excel');

  // ── 단일 등록 ──
  const [drawId, setDrawId] = useState('');
  const [drawDate, setDrawDate] = useState('');
  const [drawNumbers, setDrawNumbers] = useState('');
  const [drawBonus, setDrawBonus] = useState('');
  const [drawPrize, setDrawPrize] = useState('');
  const [drawWinners, setDrawWinners] = useState('');

  // ── 일괄 등록 ──
  const [bulkText, setBulkText] = useState('');

  // ── API 연동 ──
  const [apiStartRound, setApiStartRound] = useState('1');
  const [apiEndRound, setApiEndRound] = useState('855');
  const [apiProgress, setApiProgress] = useState('');
  const [apiRunning, setApiRunning] = useState(false);
  const [retryRounds, setRetryRounds] = useState<number[]>([]);

  const chainAbortRef = useRef(false);

  // ── 엑셀 업로드 ──
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelPreview, setExcelPreview] = useState<ExcelPreviewRow[]>([]);
  const [excelParsing, setExcelParsing] = useState(false);
  const [excelImporting, setExcelImporting] = useState(false);
  const [excelImportProgress, setExcelImportProgress] = useState('');
  const [excelDuplicateMode, setExcelDuplicateMode] = useState<ExcelDuplicateMode>('skip');
  const [existingDrawIds, setExistingDrawIds] = useState<Set<number>>(new Set());
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  const [duplicateCheckError, setDuplicateCheckError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── 자동 동기화 ──
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(() => {
    try {
      return localStorage.getItem(AUTO_SYNC_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [syncLatestLoading, setSyncLatestLoading] = useState(false);

  // ── DB 요약 ──
  const [dbSummary, setDbSummary] = useState<DbSummary | null>(null);

  // ── 페이지네이션 ──
  const [drawsPage, setDrawsPage] = useState(1);
  const [drawsTotal, setDrawsTotal] = useState(0);
  const [syncLogsPage, setSyncLogsPage] = useState(1);
  const [syncLogsTotal, setSyncLogsTotal] = useState(0);

  // ── 데이터 fetch ──

  const fetchDraws = useCallback(async (pg?: number) => {
    const p = pg ?? drawsPage;
    setLoading(true);
    try {
      const from = (p - 1) * DRAWS_PAGE_SIZE;
      const to = from + DRAWS_PAGE_SIZE - 1;
      const { data, count, error: fetchErr } = await supabase
        .from('draw_history')
        .select('*', { count: 'exact' })
        .order('id', { ascending: false })
        .range(from, to);
      if (fetchErr) throw fetchErr;
      if (data) setDraws(data as DrawRecord[]);
      if (count !== null) setDrawsTotal(count);
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 회차 조회 실패:', e);
    } finally {
      setLoading(false);
    }
  }, [drawsPage]);

  const fetchDbSummary = useCallback(async () => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_draw_data_status');
      if (rpcError || !data) {
        // fail-closed: 불완전한 summary 로 최신 회차를 판단하지 않음 → null 유지(자동 동기화 미발동)
        if (import.meta.env.DEV) console.error('[useDrawManager] DB 요약 조회 실패:', rpcError?.message ?? 'no data');
        setDbSummary(null);
        return;
      }
      const min = Number(data.min_round) || 0;
      const max = Number(data.latest_round) || 0;
      const count = Number(data.total_count) || 0;
      setDbSummary({ min, max, count });
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] DB 요약 예외:', e);
      setDbSummary(null);
    }
  }, []);

  const fetchSyncLogs = useCallback(async (pg?: number) => {
    const p = pg ?? syncLogsPage;
    setLogsLoading(true);
    try {
      const from = (p - 1) * SYNC_LOGS_PAGE_SIZE;
      const to = from + SYNC_LOGS_PAGE_SIZE - 1;
      const { data, count, error: fetchErr } = await supabase
        .from('draw_sync_log')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);
      if (fetchErr) throw fetchErr;
      if (data) setSyncLogs(data as SyncLog[]);
      if (count !== null) setSyncLogsTotal(count);
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 동기화 로그 조회 실패:', e);
    } finally {
      setLogsLoading(false);
    }
  }, [syncLogsPage]);

  const handleDrawsPageChange = useCallback((p: number) => {
    setDrawsPage(p);
  }, []);

  const handleSyncLogsPageChange = useCallback((p: number) => {
    setSyncLogsPage(p);
  }, []);

  // ── 초기 로딩 ──
  useEffect(() => {
    fetchDraws();
    fetchDbSummary();
    fetchSyncLogs();
  }, [fetchDraws, fetchDbSummary, fetchSyncLogs]);

  // ── 자동 동기화 ──
  const runAutoSync = useCallback(async (start: number, end: number) => {
    setSyncLatestLoading(true);
    setError('');
    setSuccess('');
    chainAbortRef.current = false;
    try {
      const requestId = generateRequestId();
      const res = await callApiBatch(start, end, 'auto', requestId, chainAbortRef);
      if (res.status === 'success') {
        setSuccess(`자동 동기화 완료! ${res.msg}`);
      } else if (res.status === 'partial') {
        setError(`자동 동기화 일부 실패: ${res.msg}`);
        setRetryRounds(res.failedRounds);
      } else if (res.status === 'running') {
        setError('다른 동기화가 진행 중입니다. 잠시 후 다시 시도해주세요.');
      } else {
        setError(`자동 동기화 실패: ${res.msg}`);
      }
      fetchDraws();
      fetchDbSummary();
      fetchSyncLogs();
    } catch (e) {
      setError('자동 동기화 중 오류가 발생했습니다.');
    } finally {
      setSyncLatestLoading(false);
    }
  }, [fetchDraws, fetchDbSummary, fetchSyncLogs]);

  useEffect(() => {
    if (autoSyncEnabled && dbSummary) {
      const latestExpected = getLatestExpectedRound();
      if (latestExpected > dbSummary.max) {
        const missingStart = dbSummary.max + 1;
        const missingEnd = latestExpected;
        if (missingEnd - missingStart + 1 <= 5) {
          runAutoSync(missingStart, missingEnd);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSyncEnabled, dbSummary]);

  // ── 엑셀 핸들러 ──

  const checkExistingDrawIds = useCallback(async (ids: number[]): Promise<
    | { ok: true; foundIds: Set<number> }
    | { ok: false; error: string }
  > => {
    const CHECK_BATCH_SIZE = 100;
    const found = new Set<number>();
    try {
      for (let i = 0; i < ids.length; i += CHECK_BATCH_SIZE) {
        const chunk = ids.slice(i, i + CHECK_BATCH_SIZE);
        const { data, error: queryError } = await supabase
          .from('draw_history')
          .select('id')
          .in('id', chunk);

        if (queryError) {
          if (import.meta.env.DEV) console.error('[useDrawManager] 중복 회차 조회 실패:', queryError.message);
          return { ok: false, error: '기존 회차 중복 여부 확인에 실패했습니다. 다시 시도해주세요.' };
        }

        if (data) {
          for (const row of data) {
            found.add(row.id);
          }
        }
      }
      return { ok: true, foundIds: found };
    } catch (err) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 중복 회차 조회 예외:', err);
      return { ok: false, error: '기존 회차 중복 여부 확인 중 오류가 발생했습니다.' };
    }
  }, []);

  const excelImportPlan: ExcelImportPlan = createExcelImportPlan(excelPreview, existingDrawIds, excelDuplicateMode);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
      setExcelFile(null);
      setExcelPreview([]);
      setExcelDuplicateMode('skip');
      setExistingDrawIds(new Set());
      setDuplicateCheckError('');
      setSuccess('');
      setError('지원하지 않는 파일 형식입니다. .xlsx 또는 .xls 파일을 선택해주세요.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_EXCEL_FILE_BYTES) {
      setExcelFile(null);
      setExcelPreview([]);
      setExcelDuplicateMode('skip');
      setExistingDrawIds(new Set());
      setDuplicateCheckError('');
      setSuccess('');
      setError('엑셀 파일은 10MB 이하만 업로드할 수 있습니다.');
      e.target.value = '';
      return;
    }

    setExcelFile(file);
    setExcelPreview([]);
    setExcelDuplicateMode('skip');
    setExistingDrawIds(new Set());
    setDuplicateCheckError('');
    setError('');
    setSuccess('');
    setExcelParsing(true);

    let rows: ExcelPreviewRow[] = [];
    try {
      rows = await parseExcelFile(file);
      setExcelPreview(rows);
    } catch (err) {
      const errMsg = String(err);
      setError(errMsg);
      setExcelFile(null);
      setExcelDuplicateMode('skip');
      setExistingDrawIds(new Set());
      setDuplicateCheckError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await supabase.from('error_logs').insert({
        error_type: 'excel_parse',
        error_message: errMsg,
        error_detail: JSON.stringify({ fileName: file.name, fileSize: file.size }),
        source: 'excel_import',
      });
      return;
    } finally {
      setExcelParsing(false);
    }

    if (rows.length > 0) {
      setDuplicateChecking(true);
      try {
        const checkRes = await checkExistingDrawIds(rows.map((r) => r.id));

        if (checkRes.ok === false) {
          setDuplicateCheckError(checkRes.error);
          setError(checkRes.error);
          return;
        }

        setExistingDrawIds(checkRes.foundIds);
        const initPlan = createExcelImportPlan(rows, checkRes.foundIds, 'skip');
        if (initPlan.existingCount > 0) {
          setSuccess(`엑셀 파싱 완료! ${rows.length}건 중 신규 ${initPlan.newCount}건, 기존 ${initPlan.existingCount}건을 확인했습니다.`);
        } else {
          setSuccess(`엑셀 파싱 완료! ${rows.length}건 모두 신규 회차입니다. (${rows[0].id}~${rows[rows.length - 1].id}회)`);
        }
      } finally {
        setDuplicateChecking(false);
      }
    }
  }, [checkExistingDrawIds]);

  const handleExcelImport = useCallback(async () => {
    if (excelPreview.length === 0) return;
    if (duplicateCheckError) {
      setError('기존 회차 중복 확인 오류로 인해 가져올 수 없습니다. 파일을 다시 선택해주세요.');
      return;
    }

    const plan = createExcelImportPlan(excelPreview, existingDrawIds, excelDuplicateMode);
    if (!plan.canImport || plan.targetRows.length === 0) {
      setError('가져올 대상 회차가 없습니다.');
      return;
    }

    if (excelDuplicateMode === 'overwrite' && plan.existingCount > 0) {
      const confirmed = window.confirm(
        `기존에 등록된 ${plan.existingCount}건의 회차 데이터를 덮어쓰시겠습니까?\n이 작업은 기존 당첨번호 정보를 수정합니다.`,
      );
      if (!confirmed) return;
    }

    setExcelImporting(true);
    setError('');
    setSuccess('');

    const targetRows = plan.targetRows;
    const startRound = targetRows[0].id;
    const endRound = targetRows[targetRows.length - 1].id;

    try {
      const { data: logData, error: logErr } = await supabase
        .from('draw_sync_log')
        .insert({
          start_round: startRound,
          end_round: endRound,
          total_attempted: targetRows.length,
          status: 'running',
          triggered_by: 'manual',
        })
        .select('id')
        .single();
      if (logErr) {
        if (import.meta.env.DEV) console.error('[useDrawManager] 엑셀 로그 생성 실패:', logErr.message);
      }

      const logId = logData?.id;

      let inserted = 0;
      let failed = 0;
      const BATCH_SIZE = 50;

      for (let i = 0; i < targetRows.length; i += BATCH_SIZE) {
        const batch = targetRows.slice(i, i + BATCH_SIZE);
        setExcelImportProgress(`${i + 1}~${Math.min(i + BATCH_SIZE, targetRows.length)} / ${targetRows.length}건 처리 중...`);

        const records: DrawRecord[] = batch.map((row) => ({
          id: row.id,
          draw_date: row.draw_date,
          numbers: row.numbers,
          bonus: row.bonus,
          prize_1st: row.prize_1st,
          winners_1st: row.winners_1st,
        }));

        const query = excelDuplicateMode === 'overwrite'
          ? supabase.from('draw_history').upsert(records, { onConflict: 'id' })
          : supabase.from('draw_history').insert(records);

        const { data: insertedRows, error: insertError } = await query.select('id');

        if (insertError) {
          failed += batch.length;
          await supabase.from('error_logs').insert({
            error_type: 'db_insert',
            error_message: insertError.message,
            error_detail: JSON.stringify({
              code: insertError.code,
              batch_index: i,
              batch_size: batch.length,
              mode: excelDuplicateMode,
            }),
            source: 'excel_import',
            context: { rounds: batch.map((r) => r.id) },
          });
        } else {
          inserted += (insertedRows ?? []).length;
        }
      }

      setExcelImportProgress('');

      const finalStatus = failed === 0 ? 'success' : failed < targetRows.length ? 'partial' : 'failed';

      if (logId) {
        const { error: logUpdateErr } = await supabase
          .from('draw_sync_log')
          .update({
            fetched: targetRows.length,
            inserted,
            failed,
            status: finalStatus,
            completed_at: new Date().toISOString(),
          })
          .eq('id', logId);
        if (logUpdateErr) {
          if (import.meta.env.DEV) console.error('[useDrawManager] 엑셀 로그 갱신 실패:', logUpdateErr.message);
        }
      }

      if (finalStatus === 'success') {
        const msg = formatExcelImportSuccessMessage({
          mode: excelDuplicateMode,
          inserted,
          newCount: plan.newCount,
          existingCount: plan.existingCount,
        });
        setSuccess(msg);
        setExcelFile(null);
        setExcelPreview([]);
        setExcelDuplicateMode('skip');
        setExistingDrawIds(new Set());
        setDuplicateCheckError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setError(`${inserted}건 등록, ${failed}건 실패`);
      }
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 엑셀 가져오기 예외:', e);
      setError('엑셀 가져오기 중 오류가 발생했습니다.');
    } finally {
      setExcelImporting(false);
      fetchDraws();
      fetchDbSummary();
      fetchSyncLogs();
    }
  }, [excelPreview, existingDrawIds, excelDuplicateMode, duplicateCheckError, fetchDraws, fetchDbSummary, fetchSyncLogs]);

  const handleClearExcel = useCallback(() => {
    setExcelFile(null);
    setExcelPreview([]);
    setExcelDuplicateMode('skip');
    setExistingDrawIds(new Set());
    setDuplicateChecking(false);
    setDuplicateCheckError('');
    setError('');
    setSuccess('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ── 단일 등록 ──

  const handleSingleSubmit = useCallback(async () => {
    setError('');
    setSuccess('');

    const id = parseInt(drawId, 10);
    if (isNaN(id) || id <= 0) { setError('회차는 1 이상의 숫자여야 합니다'); return; }
    if (!parseDateOnly(drawDate)) { setError('올바른 추첨일(YYYY-MM-DD)을 입력해주세요'); return; }

    const nums = parseNumbers(drawNumbers);
    if (!nums) { setError('당첨번호는 1~45 사이의 서로 다른 6개 숫자를 쉼표 또는 공백으로 구분해주세요'); return; }

    const bonus = parseInt(drawBonus, 10);
    if (isNaN(bonus) || bonus < 1 || bonus > 45 || nums.includes(bonus)) {
      setError('보너스 번호는 1~45 사이의 당첨번호와 다른 숫자여야 합니다');
      return;
    }

    const prize = drawPrize ? parseInt(drawPrize.replace(/,/g, ''), 10) : null;
    const winners = drawWinners ? parseInt(drawWinners, 10) : null;

    setSubmitting(true);
    try {
      const { data, error: insertErr } = await supabase.from('draw_history').insert({
        id,
        draw_date: drawDate,
        numbers: nums,
        bonus,
        prize_1st: prize,
        winners_1st: winners,
      }).select('id').single();
      if (insertErr || !data) {
        if (import.meta.env.DEV) console.error('[useDrawManager] 단일 등록 실패:', insertErr?.message ?? 'no row');
        setError(insertErr?.message?.includes('duplicate') ? '이미 등록된 회차입니다.' : '등록에 실패했습니다. 다시 시도해주세요.');
        return;
      }
      setSuccess(`제 ${id}회 당첨번호가 등록되었습니다.`);
      setDrawId(''); setDrawDate(''); setDrawNumbers(''); setDrawBonus(''); setDrawPrize(''); setDrawWinners('');
      fetchDraws();
      fetchDbSummary();
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 단일 등록 예외:', e);
      setError('등록에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }, [drawId, drawDate, drawNumbers, drawBonus, drawPrize, drawWinners, fetchDraws, fetchDbSummary]);

  const handleBulkSubmit = useCallback(async () => {
    setError('');
    setSuccess('');

    const lines = bulkText.trim().split('\n').filter((l) => l.trim());
    const records: Record<string, unknown>[] = [];

    for (let i = 0; i < lines.length; i++) {
      const parts = lines[i].split(/\t|,/).map((s) => s.trim());
      if (parts.length < 4) {
        setError(`${i + 1}번째 줄 형식이 잘못되었습니다. (회차, 날짜, 번호6개, 보너스)`);
        return;
      }
      const id = parseInt(parts[0], 10);
      const date = parts[1];
      const nums = parseNumbers(parts.slice(2, 8).join(','));
      const bonus = parseInt(parts[8] || parts[parts.length - 1], 10);
      if (isNaN(id) || !parseDateOnly(date) || !nums || isNaN(bonus) || bonus < 1 || bonus > 45 || nums.includes(bonus)) {
        setError(`${i + 1}번째 줄 데이터가 유효하지 않습니다. (회차, YYYY-MM-DD 날짜, 1~45 번호6개, 보너스)`);
        return;
      }
      records.push({ id, draw_date: date, numbers: nums, bonus });
    }

    if (records.length === 0) { setError('등록할 데이터가 없습니다.'); return; }

    setSubmitting(true);
    try {
      const { data, error: insertErr } = await supabase.from('draw_history').insert(records).select('id');
      if (insertErr) {
        if (import.meta.env.DEV) console.error('[useDrawManager] 일괄 등록 실패:', insertErr.message);
        setError(insertErr.message.includes('duplicate') ? '중복된 회차가 포함되어 있습니다.' : '일괄 등록에 실패했습니다. 다시 시도해주세요.');
        return;
      }
      setSuccess(`${records.length}개 회차가 일괄 등록되었습니다.`);
      setBulkText('');
      fetchDraws();
      fetchDbSummary();
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 일괄 등록 예외:', e);
      setError('일괄 등록에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }, [bulkText, fetchDraws, fetchDbSummary]);

  // ── API 연동 ──

  const runChunked = useCallback(async (
    chunks: { s: number; e: number }[],
    trigger: string,
  ): Promise<ChainResult> => {
    const results: ApiBatchResult[] = [];

    for (let i = 0; i < chunks.length; i++) {
      if (chainAbortRef.current) {
        results.push(abortedResult());
        break;
      }
      setApiProgress(`진행 중... (${i + 1}/${chunks.length}) ${chunks[i].s}~${chunks[i].e}회차 가져오는 중`);
      const requestId = generateRequestId();
      const res = await callApiBatch(chunks[i].s, chunks[i].e, trigger, requestId, chainAbortRef);
      results.push(res);
      if (res.status !== 'success' && res.status !== 'partial' && res.status !== 'failed') {
        break;
      }
      if (i < chunks.length - 1 && !chainAbortRef.current) {
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    setApiProgress('');

    return aggregateSyncChain(chunks, results);
  }, []);

  const applyChainOutcome = useCallback((cr: ChainResult, label: string) => {
    const retryable = [...new Set([...cr.failedRounds, ...cr.untouchedRounds])].sort((a, b) => a - b);
    switch (cr.status) {
      case 'success':
        setSuccess(`${label} 완료! (${cr.inserted}건 등록)`);
        setRetryRounds([]);
        break;
      case 'aborted':
        if (cr.inserted > 0) {
          setError(`${label}이 중단되었습니다. 일부 회차만 처리되었습니다. (${cr.inserted}건 등록, ${retryable.length}건 미처리)`);
        } else {
          setError(`${label}이 중단되었습니다. (${retryable.length}건 미처리)`);
        }
        setRetryRounds(retryable);
        break;
      case 'running':
        setError(`${label}: 다른 동기화가 진행 중이라 중단되었습니다.${cr.inserted > 0 ? ` (${cr.inserted}건 등록)` : ''} (${retryable.length}건 미처리)`);
        setRetryRounds(retryable);
        break;
      case 'error':
        setError(`${label} 중 오류가 발생해 중단되었습니다.${cr.inserted > 0 ? ` (${cr.inserted}건 등록)` : ''} (${retryable.length}건 미처리)`);
        setRetryRounds(retryable);
        break;
      case 'partial':
        setError(`${label} 일부 실패 (${cr.failed}건 실패, ${cr.inserted}건 등록). 아래 버튼으로 실패 회차를 재시도할 수 있습니다.`);
        setRetryRounds(retryable);
        break;
      default:
        setError(`${label} 실패 (${cr.failed}건). 아래 버튼으로 재시도할 수 있습니다.`);
        setRetryRounds(retryable);
        break;
    }
  }, []);

  const handleApiFetch = useCallback(async () => {
    setError('');
    setSuccess('');
    setApiProgress('');
    setRetryRounds([]);

    const start = parseInt(apiStartRound, 10);
    const end = parseInt(apiEndRound, 10);

    if (isNaN(start) || isNaN(end) || start < 1 || end > MAX_ROUND || start > end) {
      setError(`올바른 회차 범위를 입력해주세요. (1~${MAX_ROUND})`);
      return;
    }

    const chunks: { s: number; e: number }[] = [];
    for (let r = start; r <= end; r += CHUNK_SIZE) {
      chunks.push({ s: r, e: Math.min(r + CHUNK_SIZE - 1, end) });
    }

    chainAbortRef.current = false;
    setApiRunning(true);
    try {
      const chainResult = await runChunked(chunks, 'manual');
      applyChainOutcome(chainResult, '전체 동기화');
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] API 동기화 예외:', e);
      setError('동기화 중 오류가 발생했습니다.');
    } finally {
      setApiRunning(false);
      fetchDraws();
      fetchDbSummary();
      fetchSyncLogs();
    }
  }, [apiStartRound, apiEndRound, runChunked, applyChainOutcome, fetchDraws, fetchDbSummary, fetchSyncLogs]);

  const handleRetryFailedRounds = useCallback(async () => {
    if (retryRounds.length === 0) return;
    setError('');
    setSuccess('');
    setApiProgress('');

    const chunks: number[][] = [];
    for (let i = 0; i < retryRounds.length; i += CHUNK_SIZE) {
      chunks.push(retryRounds.slice(i, i + CHUNK_SIZE));
    }

    chainAbortRef.current = false;
    setApiRunning(true);
    try {
      const failedRounds: number[] = [];
      const untouchedRounds: number[] = [];
      let stopReason: 'aborted' | 'running' | 'error' | null = null;

      for (let i = 0; i < chunks.length; i++) {
        if (chainAbortRef.current) {
          stopReason = 'aborted';
          for (let j = i; j < chunks.length; j++) untouchedRounds.push(...chunks[j]);
          break;
        }
        setApiProgress(`실패 회차 재시도 중... (${i + 1}/${chunks.length})`);
        const requestId = generateRequestId();
        const res = await callApiRounds(chunks[i], 'manual', requestId, chainAbortRef);

        if (res.status === 'success') {
          // 이 청크의 회차는 모두 등록됨
        } else if (res.status === 'partial' || res.status === 'failed') {
          failedRounds.push(...res.failedRounds);
        } else if (res.status === 'running') {
          stopReason = 'running';
          for (let j = i; j < chunks.length; j++) untouchedRounds.push(...chunks[j]);
          break;
        } else if (res.status === 'aborted') {
          stopReason = 'aborted';
          for (let j = i; j < chunks.length; j++) untouchedRounds.push(...chunks[j]);
          break;
        } else {
          stopReason = 'error';
          for (let j = i; j < chunks.length; j++) untouchedRounds.push(...chunks[j]);
          break;
        }

        if (i < chunks.length - 1) await new Promise((r) => setTimeout(r, 1500));
      }

      const remaining = [...new Set([...failedRounds, ...untouchedRounds])].sort((a, b) => a - b);

      if (stopReason === 'aborted') {
        setError(`재시도가 중단되었습니다. 일부 회차만 복구되었습니다. (${remaining.length}건 미처리)`);
        setRetryRounds(remaining);
      } else if (stopReason === 'running' || stopReason === 'error') {
        setError(`재시도 중 오류로 중단되었습니다. 일부 회차만 복구되었습니다. (${remaining.length}건 미처리)`);
        setRetryRounds(remaining);
      } else if (remaining.length === 0) {
        setSuccess('재시도 완료! 모든 실패 회차가 등록되었습니다.');
        setRetryRounds([]);
      } else {
        setError(`여전히 ${remaining.length}건이 실패했습니다. 다시 시도해주세요.`);
        setRetryRounds(remaining);
      }
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 재시도 예외:', e);
      setError('재시도 중 오류가 발생했습니다.');
    } finally {
      setApiRunning(false);
      setApiProgress('');
      fetchDraws();
      fetchDbSummary();
      fetchSyncLogs();
    }
  }, [retryRounds, fetchDraws, fetchDbSummary, fetchSyncLogs]);

  const handleAbortApi = useCallback(() => {
    chainAbortRef.current = true;
  }, []);

  // ── 최신 동기화 ──

  const handleSyncLatest = useCallback(async () => {
    setError('');
    setSuccess('');
    setSyncLatestLoading(true);

    try {
      const { data: maxData, error: maxErr } = await supabase
        .from('draw_history')
        .select('id')
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (maxErr) throw maxErr;

      const dbMax = maxData?.id || 0;
      const expectedMax = getLatestExpectedRound();

      if (dbMax >= expectedMax) {
        setSuccess('이미 최신 상태입니다! 모든 회차가 등록되어 있어요.');
        return;
      }

      const missingStart = dbMax + 1;
      const missingEnd = expectedMax;
      const missingCount = missingEnd - missingStart + 1;

      chainAbortRef.current = false;

      if (missingCount > CHUNK_SIZE) {
        const chunks: { s: number; e: number }[] = [];
        for (let r = missingStart; r <= missingEnd; r += CHUNK_SIZE) {
          chunks.push({ s: r, e: Math.min(r + CHUNK_SIZE - 1, missingEnd) });
        }
        const chainResult = await runChunked(chunks, 'manual');
        applyChainOutcome(chainResult, '최신 동기화');
      } else {
        setApiProgress(`${missingStart}~${missingEnd}회차 동기화 중...`);
        const requestId = generateRequestId();
        const res = await callApiBatch(missingStart, missingEnd, 'manual', requestId, chainAbortRef);

        if (res.status === 'success') {
          setSuccess(`최신 동기화 완료! ${res.msg}`);
          setRetryRounds([]);
        } else if (res.status === 'partial') {
          setError(`최신 동기화 일부 실패: ${res.msg}`);
          setRetryRounds(res.failedRounds);
        } else if (res.status === 'running') {
          setError('다른 동기화가 진행 중입니다. 잠시 후 다시 시도해주세요.');
        } else {
          setError(`최신 동기화 실패: ${res.msg}`);
        }
      }

      fetchDraws();
      fetchDbSummary();
      fetchSyncLogs();
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 최신 동기화 예외:', e);
      setError('동기화 중 오류가 발생했습니다.');
    } finally {
      setSyncLatestLoading(false);
      setApiProgress('');
    }
  }, [runChunked, applyChainOutcome, fetchDraws, fetchDbSummary, fetchSyncLogs]);

  // ── 삭제 ──

  const handleDelete = useCallback(async (id: number) => {
    if (!confirm(`제 ${id}회 데이터를 삭제할까요?`)) return;
    setError('');
    setSuccess('');
    try {
      const { data, error: delErr } = await supabase.from('draw_history').delete().eq('id', id).select('id');
      if (delErr) {
        if (import.meta.env.DEV) console.error('[useDrawManager] 삭제 실패:', delErr.message);
        setError('삭제에 실패했습니다. 다시 시도해주세요.');
        return;
      }
      if (!data || data.length === 0) {
        setError('대상 회차를 찾을 수 없습니다.');
        return;
      }
      setSuccess(`제 ${id}회 데이터가 삭제되었습니다.`);
      fetchDraws();
      fetchDbSummary();
    } catch (e) {
      if (import.meta.env.DEV) console.error('[useDrawManager] 삭제 예외:', e);
      setError('삭제에 실패했습니다. 다시 시도해주세요.');
    }
  }, [fetchDraws, fetchDbSummary]);

  // ── 자동 동기화 토글 ──

  const toggleAutoSync = useCallback(() => {
    setAutoSyncEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(AUTO_SYNC_STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // ── 모드 전환 시 에러/성공 초기화 ──

  const changeMode = useCallback((newMode: DrawMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
  }, []);

  // ── Return ──

  return {
    // 공통
    draws, loading, submitting, error, setError, success, setSuccess,
    syncLogs, logsLoading,
    mode, setMode: changeMode,
    dbSummary, autoSyncEnabled, syncLatestLoading,

    // pagination
    drawsPage, drawsTotal, drawsPageSize: DRAWS_PAGE_SIZE,
    onDrawsPageChange: handleDrawsPageChange,
    syncLogsPage, syncLogsTotal, syncLogsPageSize: SYNC_LOGS_PAGE_SIZE,
    onSyncLogsPageChange: handleSyncLogsPageChange,

    // fetch
    fetchDraws, fetchDbSummary, fetchSyncLogs,

    // 엑셀
    excelFile, excelPreview, excelParsing, excelImporting, excelImportProgress,
    excelDuplicateMode, setExcelDuplicateMode,
    excelImportPlan, duplicateChecking, duplicateCheckError,
    fileInputRef,
    handleFileSelect, handleExcelImport, handleClearExcel,

    // API
    apiStartRound, setApiStartRound, apiEndRound, setApiEndRound,
    apiProgress, setApiProgress, apiRunning,
    handleApiFetch, handleAbortApi, retryRounds, handleRetryFailedRounds,

    // 단일/일괄 폼
    drawId, setDrawId, drawDate, setDrawDate,
    drawNumbers, setDrawNumbers, drawBonus, setDrawBonus,
    drawPrize, setDrawPrize, drawWinners, setDrawWinners,
    bulkText, setBulkText,
    handleSingleSubmit, handleBulkSubmit,

    // 기타
    handleSyncLatest, handleDelete, toggleAutoSync,
  };
}
